// apps/frontend/src/components/wallet/WalletBadge.tsx
// Badge solde avec dopamine loop — visible en permanence dans le header

import React from 'react';
import { Wallet, TrendingUp } from 'lucide-react';
import { useWallet } from '../../hooks/useWallet';

interface WalletBadgeProps {
  onClick: () => void;
}

function fmt(amount: number, currency = 'EUR') {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export const WalletBadge: React.FC<WalletBadgeProps> = ({ onClick }) => {
  const { info, loadingInfo } = useWallet();

  const hasActivity =
    info && (info.todayEarnings > 0 || info.weekSalesCount > 0);

  const subline = info
    ? info.todayEarnings > 0
      ? `+${fmt(info.todayEarnings, info.currency)} aujourd'hui`
      : info.weekSalesCount > 0
        ? `${info.weekSalesCount} vente${info.weekSalesCount > 1 ? 's' : ''} cette semaine`
        : null
    : null;

  return (
    <button
      onClick={onClick}
      title="Mon portefeuille"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all duration-200 group ${
        hasActivity
          ? 'border-brand-border bg-brand-cream hover:border-brand-500 hover:bg-brand-cream'
          : 'border-brand-500/30 hover:border-brand-500 hover:bg-[#F5F0E8]'
      }`}
    >
      {hasActivity ? (
        <TrendingUp className="w-4 h-4 text-brand-500 flex-shrink-0" />
      ) : (
        <Wallet className="w-4 h-4 text-brand-500 flex-shrink-0" />
      )}

      {loadingInfo && !info ? (
        <span className="w-14 h-3 bg-brand-light rounded animate-pulse" />
      ) : (
        <div className="flex flex-col items-start leading-none">
          <span
            className={`text-sm font-semibold transition-colors ${
              hasActivity
                ? 'text-brand-dark'
                : 'text-brand-dark group-hover:text-brand-500'
            }`}
          >
            {info ? fmt(info.balance, info.currency) : '—'}
          </span>
          {subline && (
            <span className="text-[10px] font-medium text-brand-500 whitespace-nowrap">
              {subline}
            </span>
          )}
        </div>
      )}

      {info?.isFrozen && (
        <span className="text-xs text-red-500" title="Wallet gelé">
          🔒
        </span>
      )}
    </button>
  );
};

export default WalletBadge;
